/**
 * CR308: Latest S2 Attempt Hook
 * 
 * Fetches the most recent S2 (Website Collect) attempt for a batch run
 * and derives the correct status/metrics based on that attempt.
 * 
 * This ensures the UI reflects the latest retry outcome, not historical failures.
 */
import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface S2AttemptInfo {
  id: string;
  attempt_no: number;
  status: string;
  records_in: number | null;
  records_out: number | null;
  error_message: string | null;
  error_code: string | null;
  diagnostics_json: Record<string, unknown> | null;
  created_at: string;
  started_at: string | null;
  finished_at: string | null;
  duration_ms: number | null;
  merchant_source_id: string | null;
}

export interface S2AttemptsSummary {
  latestAttempt: S2AttemptInfo | null;
  allAttempts: S2AttemptInfo[];
  totalAttempts: number;
  hasEarlierFailures: boolean;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Derive effective S2 status from the latest attempt.
 * The step's status should reflect the most recent attempt, not aggregated history.
 */
export function deriveEffectiveS2Status(
  stepStatus: string,
  latestAttempt: S2AttemptInfo | null
): {
  effectiveStatus: string;
  statusSource: "step" | "latest_attempt";
  attemptOverride: boolean;
} {
  // If no attempt data, use step status as-is
  if (!latestAttempt) {
    return {
      effectiveStatus: stepStatus,
      statusSource: "step",
      attemptOverride: false,
    };
  }

  // Map attempt status to step status
  const attemptToStepStatus: Record<string, string> = {
    COMPLETE: "COMPLETE",
    PARTIAL: "PARTIAL",
    FAILED: "FAILED",
    RUNNING: "RUNNING",
    PENDING: "PENDING",
    TIMED_OUT: "PARTIAL", // Timeout with partial output = PARTIAL
  };

  const derivedStatus = attemptToStepStatus[latestAttempt.status] || stepStatus;
  
  // Check if we're overriding a failed step status with a successful attempt
  const attemptOverride = 
    (stepStatus === "FAILED" || stepStatus === "PARTIAL") &&
    (derivedStatus === "COMPLETE");

  return {
    effectiveStatus: derivedStatus,
    statusSource: "latest_attempt",
    attemptOverride,
  };
}

export function useLatestS2Attempt(batchRunId: string | null): S2AttemptsSummary {
  const [allAttempts, setAllAttempts] = useState<S2AttemptInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchAttempts = useCallback(async () => {
    if (!batchRunId) {
      setAllAttempts([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { data, error: fetchError } = await supabase
        .from("cr075_batch_run_step_attempts")
        .select(`
          id,
          attempt_no,
          status,
          records_in,
          records_out,
          error_message,
          error_code,
          diagnostics_json,
          created_at,
          started_at,
          finished_at,
          duration_ms,
          merchant_source_id
        `)
        .eq("batch_run_id", batchRunId)
        .eq("step_key", "S2")
        .order("created_at", { ascending: false });

      if (fetchError) {
        throw new Error(fetchError.message);
      }

      setAllAttempts((data || []) as S2AttemptInfo[]);
    } catch (err) {
      console.error("[useLatestS2Attempt] Error:", err);
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [batchRunId]);

  useEffect(() => {
    fetchAttempts();
  }, [fetchAttempts]);

  // Derive summary values
  const latestAttempt = allAttempts.length > 0 ? allAttempts[0] : null;
  const hasEarlierFailures = allAttempts.some(
    (a, i) => i > 0 && (a.status === "FAILED" || a.status === "TIMED_OUT")
  );

  return {
    latestAttempt,
    allAttempts,
    totalAttempts: allAttempts.length,
    hasEarlierFailures,
    loading,
    error,
    refetch: fetchAttempts,
  };
}
